import { useState } from 'react';
import { Image } from '../../../image';
import {
	ContentSliderCard,
	ContentSliderWrapper,
	ContentSliderViewport,
	ContentSliderTrack,
	ContentSliderSlide,
	ContentSliderArrow,
	ContentSliderContent,
	ContentSliderHeading,
	ContentSliderDescription,
	ContentSliderDotNav,
	ContentSliderDot,
	LinkButtonStyled,
} from './contentSlider.style';

export type ContentSliderItem = {
	id: string;
	title: string;
	description: string;
	imageUrl?: string;
	buttonLabel: string;
	buttonUrl: string;
	openInNewWindow?: boolean;
	disabled?: boolean;
};

type SlideProps = {
	item: ContentSliderItem;
	count: number;
};

const Slide = ({ item, count }: SlideProps) => (
	<ContentSliderSlide $count={count}>
		{item.imageUrl && (
			<Image src={item.imageUrl} alt={item.title} aspectRatio="16/9" />
		)}
		<ContentSliderContent>
			{item.title && (
				<ContentSliderHeading>{item.title}</ContentSliderHeading>
			)}
			{item.description && (
				<ContentSliderDescription>{item.description}</ContentSliderDescription>
			)}
			{item.buttonUrl && (
				<LinkButtonStyled
					href={item.buttonUrl}
					target={item.openInNewWindow ? '_blank' : '_self'}
					rel="noopener noreferrer"
					variant="primary"
					label={item.buttonLabel}
					icon="arrow-right"
					iconPosition="right"
					disabled={item.disabled}
				/>
			)}
		</ContentSliderContent>
	</ContentSliderSlide>
);

export const ContentSlider = ({ items = [] }: { items?: ContentSliderItem[] }) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const count = items.length;

	if (count === 0) {
		return null;
	}

	const safeIndex = ((activeIndex % count) + count) % count;

	const prev = () => setActiveIndex((safeIndex - 1 + count) % count);
	const next = () => setActiveIndex((safeIndex + 1) % count);

	return (
		<ContentSliderWrapper>
			<ContentSliderCard>
				<ContentSliderViewport>
					<ContentSliderTrack $index={safeIndex} $count={count}>
						{items.map((item) => (
							<Slide key={item.id} item={item} count={count} />
						))}
					</ContentSliderTrack>
				</ContentSliderViewport>
				{count > 1 && (
					<ContentSliderDotNav>
						{items.map((_, i) => (
							<ContentSliderDot
								key={i}
								$active={i === safeIndex}
								onClick={() => setActiveIndex(i)}
								aria-label={`Go to slide ${i + 1}`}
							/>
						))}
					</ContentSliderDotNav>
				)}
			</ContentSliderCard>
			{count > 1 && (
				<>
					<ContentSliderArrow
						$dir="left"
						icon="chevron-left"
						variant="white"
						size="small"
						onClick={prev}
						aria-label="Previous slide"
					/>
					<ContentSliderArrow
						$dir="right"
						icon="chevron-right"
						variant="white"
						size="small"
						onClick={next}
						aria-label="Next slide"
					/>
				</>
			)}
		</ContentSliderWrapper>
	);
};
